import React, { useEffect, useRef, useState } from 'react';
import { getFilters } from '../api';
import MultiSelectFilter from './MultiSelectFilter';
import FiltrosDeConducta, { clavesDeConducta } from './FiltrosDeConducta';
import FiltroDeAgentes, { agentesFueraPorOmision, paramsDeAgentes } from './FiltroDeAgentes';
import { guardarFiltros, inicial } from '../utils/filtrosPersistentes';

// Fecha local en formato YYYY-MM-DD (para los <input type="date">).
const localDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Inicio de la semana en curso (lunes), hora local. Se exporta para que una
// pantalla pueda saber si el rango sigue en el default de recarga.
export const currentWeekStart = () => {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7; // lunes=0 ... domingo=6
  d.setDate(d.getDate() - dow);
  return localDate(d);
};

/**
 * Barra de filtros compartida para las pestañas de análisis.
 *
 * Mantiene el estado de todos los filtros disponibles y emite, vía onChange,
 * un objeto de params listo para las llamadas API (arrays multivalor +
 * direction + rango de fechas). Carga las opciones de /calls/filters.
 *
 * Los filtros SOBREVIVEN a la navegación y NO a la recarga: si filtras un mes,
 * abres una llamada y vuelves, el rango sigue puesto; si recargas, vuelve el
 * default de la semana en curso. Ver `utils/filtrosPersistentes.js`.
 *
 * @param {(params: object) => void} props.onChange
 * @param {string} [props.persistKey] Clave de la memoria de filtros. Por default
 *   la ruta actual, así que cada pantalla recuerda los suyos. Dos pantallas que
 *   quieran compartirlos pasan la misma clave.
 * @param {boolean} [props.vistaDelAgente=false] Mis llamadas del Agente
 *   (14-oct-2026): la misma barra SIN el filtro de agentes —el agente sólo ve
 *   las suyas, y eso lo hace valer el backend— y con las conductas en primera
 *   persona. No manda `agent` ni `agent_excluir`: en «ver como agente» quien
 *   navega es un admin, y la exclusión por omisión de su rol no tiene nada que
 *   hacer sobre las llamadas de un solo agente.
 */
/**
 * Un grupo de filtros, con su rótulo. Los nombres los fijó Samuel el
 * 31-ago-2026 y son los que se usan al hablar de esto, así que se escriben tal
 * cual — no se "mejoran" aquí.
 *
 * Por qué agrupar en vez de esconder
 * ----------------------------------
 * La barra tenía 18 controles en una fila plana, por acumulación: "Responsable
 * queja" había quedado separado de "Quejas" por el filtro de tipificación, que
 * no tiene nada que ver con quejas.
 *
 * Se agrupa SIN esconder nada. Un segundo nivel detrás de "más filtros" ahorra
 * altura, pero los filtros se persisten en `localStorage` por pantalla: alguien
 * puede volver el lunes con un filtro puesto del jueves, y si está escondido el
 * tablero muestra cifras recortadas con la causa fuera de la vista. Es el mismo
 * defecto que el botón del falso positivo —un control que no se encuentra— pero
 * peor, porque ahí lo que se pierde son números. Si la altura llega a estorbar
 * en uso, el segundo nivel se agrega DESPUÉS, y con los activos promovidos a la
 * fila visible.
 */
function GrupoDeFiltros({ titulo, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontSize: 9.5, letterSpacing: 0.5, textTransform: 'uppercase',
        color: '#6b7280', fontWeight: 600, paddingLeft: 2,
      }}>
        {titulo}
      </span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {children}
      </div>
    </div>
  );
}


/**
 * Preselección sobre «Toda conclusion» (24-sep-2026): Comerciales · Públicas |
 * Venta · No venta · Otros. NO es un filtro nuevo: marca y desmarca etiquetas
 * del dropdown que ya existe, y lo que viaja al API sigue siendo
 * `conclusion_code`.
 *
 * Qué etiqueta cae en qué grupo lo dice el backend (`conclusion_presets` de
 * /calls/filters, definido en `tipificacion.PRESELECCION_CONCLUSION_V2`): aquí
 * no se copia el catálogo.
 *
 * Los botones se suman: (rubros presionados, o los dos si ninguno) ×
 * (desenlaces presionados, o los tres si ninguno). «Comerciales» + «Venta» es
 * sólo la venta comercial; «Venta» sola es la venta de los dos rubros.
 */
const RUBROS_PRESELECCION = [
  { id: 'comerciales', texto: 'Comerciales' },
  { id: 'publicas', texto: 'Públicas' },
];
const DESENLACES_PRESELECCION = [
  { id: 'venta', texto: 'Venta' },
  { id: 'no_venta', texto: 'No venta' },
  { id: 'otros', texto: 'Otros' },
];
const PRESELECCION_VACIA = { rubros: [], desenlaces: [] };
// Con lo que abre la barra cuando la pantalla no tiene nada guardado (primera
// visita o recarga), decidido por Samuel el 28-sep-2026. Es SÓLO el inicial:
// lo que se elija después se conserva al navegar, igual que el resto.
const PRESELECCION_INICIAL = { rubros: ['publicas'], desenlaces: ['venta', 'no_venta'] };

const etiquetasDePreseleccion = (presets, { rubros, desenlaces }) => {
  if (!rubros.length && !desenlaces.length) return [];
  return presets
    .filter((p) => (!rubros.length || rubros.includes(p.rubro))
      && (!desenlaces.length || desenlaces.includes(p.desenlace)))
    .map((p) => p.label);
};

const mismoConjunto = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

// Lo guardado puede venir de una versión anterior o estar corrupto: sin forma
// válida se arranca apagado, que es lo que no le cambia el filtro a nadie.
const preseleccionValida = (v) => (
  v && Array.isArray(v.rubros) && Array.isArray(v.desenlaces) ? v : PRESELECCION_VACIA
);

function BotonDePreseleccion({ texto, activo, disabled, title, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={activo}
      title={title}
      style={{
        padding: '6px 14px', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        border: `1px solid ${activo ? '#818cf8' : '#2a2a38'}`,
        background: activo ? 'rgba(129, 140, 248, 0.16)' : 'transparent',
        color: disabled ? '#4b5563' : (activo ? '#c7d2fe' : '#9ca3af'),
      }}
    >
      {texto}
    </button>
  );
}


export default function AnalyticsFilterBar({
  onChange, persistKey, showTypification = false, preseleccionTipificacion = false,
  showConducta = false,
  rangoSolicitado = null,
  vistaDelAgente = false,
}) {
  // Se captura UNA vez: `persistKey` no debe cambiar durante la vida del
  // componente, o el estado quedaría escrito bajo una clave y leído de otra.
  const claveRef = useRef(persistKey || window.location.pathname);
  const clave = claveRef.current;
  const [opts, setOpts] = useState({});
  const [direction, setDirection] = useState(() => inicial(clave, 'direction', 'inbound'));
  // Por defecto: semana en curso (lunes → hoy). Evita que Overview/Llamadas y
  // demás pestañas traigan TODO el histórico al abrir (lento). El usuario puede
  // mover o limpiar el filtro para ver cualquier rango.
  const [dateFrom, setDateFrom] = useState(() => inicial(clave, 'dateFrom', currentWeekStart()));
  const [dateTo, setDateTo] = useState(() => inicial(clave, 'dateTo', localDate(new Date())));
  const [agent, setAgent] = useState(() => inicial(clave, 'agent', []));
  // Los agentes marcados se guardan de dos formas, y sólo una vale a la vez:
  // `agent` (los elegidos) o `agentExcluido` (todos menos éstos). Ver
  // `components/FiltroDeAgentes.jsx` para por qué la segunda no es la primera.
  const [excluidosInicial] = useState(agentesFueraPorOmision);
  const [agentExcluido, setAgentExcluido] = useState(() => inicial(clave, 'agentExcluido', excluidosInicial));
  // El String() no es decorativo: la version rota persistio un arreglo bajo
  // esta clave, y `inicial` devuelve lo guardado tal cual. Sin la coercion, a
  // quien ya abrio la pagina le llega [] y el <select> recibe un arreglo.
  const [typification, setTypification] = useState(() => {
    const v = inicial(clave, 'typification', '');
    return typeof v === 'string' ? v : '';
  });
  const [queue, setQueue] = useState(() => inicial(clave, 'queue', []));
  const [hotel, setHotel] = useState(() => inicial(clave, 'hotel', []));
  const [crece, setCrece] = useState(() => inicial(clave, 'crece', []));
  const [tariff, setTariff] = useState(() => inicial(clave, 'tariff', []));
  const [sentiment, setSentiment] = useState(() => inicial(clave, 'sentiment', []));
  const [category, setCategory] = useState(() => inicial(clave, 'category', []));
  const [conclusion, setConclusion] = useState(() => inicial(clave, 'conclusion', []));
  // Sin nada guardado, la preselección inicial está pendiente hasta que llegue
  // de /calls/filters qué etiqueta cae en qué grupo. Mientras tanto la barra no
  // emite: emitir sin la conclusión haría una consulta sin filtrar y, en
  // seguida, la filtrada — y la primera puede llegar después y pisarla.
  const [preseleccionPendiente, setPreseleccionPendiente] = useState(
    () => preseleccionTipificacion && inicial(clave, 'conclusion', null) === null,
  );
  // Qué botones de preselección se presionaron. No viaja al API: lo que filtra
  // es `conclusion`. Ver `preseleccionVigente` para cuándo cuenta.
  const [preseleccion, setPreseleccion] = useState(
    () => (preseleccionPendiente
      ? PRESELECCION_INICIAL
      : preseleccionValida(inicial(clave, 'preseleccion', PRESELECCION_VACIA))),
  );
  const [spam, setSpam] = useState(() => inicial(clave, 'spam', ''));  // '' | 'true' | 'false'
  const [spamSubtype, setSpamSubtype] = useState(() => inicial(clave, 'spamSubtype', []));
  const [complaints, setComplaints] = useState(() => inicial(clave, 'complaints', ''));  // '' | 'true'
  const [complaintTarget, setComplaintTarget] = useState(() => inicial(clave, 'complaintTarget', []));
  // Aprovechamiento: vacío = sin filtro, y en ese caso cada tablero aplica su
  // propia exclusión de negocio. Este filtro sirve para MIRAR las no
  // aprovechables (auditar la regla), no para decidir si cuentan.
  const [aprovechable, setAprovechable] = useState(() => inicial(clave, 'aprovechable', ''));
  // Réplica del agente: '' | 'desacuerdo' | 'con' | 'sin'. Vacío = sin filtro.
  const [replica, setReplica] = useState(() => inicial(clave, 'replica', ''));
  // Conductas de la boleta V2 (sólo donde la pantalla pide `showConducta`).
  // Se guardan por ETIQUETA, que es lo que muestra el selector, y viajan al API
  // como clave de la rúbrica (`opts.conductas_v2`). Varias = todas a la vez.
  const conductasGuardadas = (k) => {
    const v = showConducta ? inicial(clave, k, []) : [];
    return Array.isArray(v) ? v : [];
  };
  const [conductaSi, setConductaSi] = useState(() => conductasGuardadas('conductaSi'));
  const [conductaNo, setConductaNo] = useState(() => conductasGuardadas('conductaNo'));
  // Búsqueda por ID de llamada (conversation_id de Genesys o # interno).
  const [callId, setCallId] = useState(() => inicial(clave, 'callId', ''));
  // Valor "debounced" que realmente viaja al API. Sin esto, cada tecla emitiría
  // params → refetch en todas las pestañas (misma ráfaga que ya rompió el
  // buscador de transcripción). Solo emite 400ms después de la última tecla y
  // con 2+ caracteres; vacío limpia el filtro.
  const [debouncedCallId, setDebouncedCallId] = useState('');

  // Se recarga al cambiar el RANGO, no sólo al montar: las opciones de
  // conclusión dependen de él (ver `getFilters`). Es una petición por cambio de
  // fecha, y el caché de respuesta del backend (60 s, por path+query) absorbe
  // el ir y venir entre dos rangos que ya se miraron.
  useEffect(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    getFilters(params).then(r => setOpts(r.data)).catch(() => setPreseleccionPendiente(false));
  }, [dateFrom, dateTo]);

  // La preselección inicial se aplica UNA vez, con las etiquetas que manda el
  // backend. Si no llegan (backend sin `conclusion_presets`), se arranca sin
  // ella en vez de dejar la barra sin emitir.
  useEffect(() => {
    if (!preseleccionPendiente || !Object.keys(opts).length) return;
    setConclusion(etiquetasDePreseleccion(opts.conclusion_presets || [], PRESELECCION_INICIAL));
    setPreseleccionPendiente(false);
  }, [opts, preseleccionPendiente]);

  // Una pantalla puede pedir un rango (Cumplimiento → Evolución abre con 30
  // días). Cada objeto nuevo es una petición; el rango sigue siendo de la barra
  // y se ve y se cambia aquí como cualquier otro.
  useEffect(() => {
    if (!rangoSolicitado) return;
    setDateFrom(rangoSolicitado.dateFrom);
    setDateTo(rangoSolicitado.dateTo);
  }, [rangoSolicitado]);

  useEffect(() => {
    const term = callId.trim();
    const t = setTimeout(() => setDebouncedCallId(term.length >= 2 ? term : ''), 400);
    return () => clearTimeout(t);
  }, [callId]);

  // Guardamos onChange en un ref para NO depender de su identidad en el efecto
  // de abajo. Si lo tuviéramos en las dependencias y el padre pasa una función
  // inline (nueva en cada render), el efecto se re-dispararía, llamaría a
  // onChange → el padre re-renderiza → nueva función → loop infinito de
  // re-render + re-fetch (el "parpadeo" reportado y una tormenta de requests).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // El catálogo de conductas como TEXTO: las opciones se vuelven a pedir en
  // cada cambio de fecha y llegan como arreglo nuevo con el mismo contenido.
  // Depender del arreglo emitiría los filtros otra vez —otra consulta— sin
  // que nada haya cambiado.
  const catalogoConductas = JSON.stringify(opts.conductas_v2 || []);

  // Emitir params cada vez que cambia un FILTRO (no cuando cambia onChange).
  useEffect(() => {
    if (preseleccionPendiente) return;
    const params = {};
    // Enviar siempre direction (incl. 'both'): los endpoints default-ean a
    // 'inbound', así que 'both' debe ir explícito para desactivar el filtro.
    if (direction) params.direction = direction;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (!vistaDelAgente) Object.assign(params, paramsDeAgentes(agent, agentExcluido));
    if (queue.length) params.queue = queue;
    if (hotel.length) params.hotel = hotel;
    if (crece.length) params.crece_classification = crece;
    if (tariff.length) params.customer_tariff_type = tariff;
    if (sentiment.length) params.sentiment = sentiment;
    if (category.length) params.call_category = category;
    if (conclusion.length) params.conclusion_code = conclusion;
    if (spam === 'true') params.is_spam = true;
    else if (spam === 'false') params.is_spam = false;
    if (spamSubtype.length) params.spam_subtype = spamSubtype;
    if (complaints === 'true') params.has_complaints = true;
    if (complaintTarget.length) params.complaint_target = complaintTarget;
    if (aprovechable === 'true') params.es_aprovechable = true;
    else if (aprovechable === 'false') params.es_aprovechable = false;
    if (typification) params.typification = typification;
    if (replica) params.replica = replica;
    // De etiqueta a clave (ver `clavesDeConducta`).
    const aClaves = (etiquetas) => clavesDeConducta(JSON.parse(catalogoConductas), etiquetas);
    if (aClaves(conductaSi).length) params.conducta_presente = aClaves(conductaSi);
    if (aClaves(conductaNo).length) params.conducta_no_presente = aClaves(conductaNo);
    if (debouncedCallId) params.call_id = debouncedCallId;
    guardarFiltros(clave, {
      direction, dateFrom, dateTo, agent, agentExcluido, typification, queue, hotel, crece, tariff, sentiment,
      category, conclusion, preseleccion, spam, spamSubtype, complaints, complaintTarget,
      aprovechable, replica, callId, conductaSi, conductaNo,
    });
    onChangeRef.current(params);
  }, [preseleccionPendiente, vistaDelAgente, clave, callId, direction, dateFrom, dateTo, agent, agentExcluido, typification, queue, hotel, crece, tariff, sentiment, category, conclusion, preseleccion, spam, spamSubtype, complaints, complaintTarget, aprovechable, replica, conductaSi, conductaNo, catalogoConductas, debouncedCallId]);

  // Devuelve TODO a su valor inicial de recarga: semana en curso, entrantes, la
  // preselección inicial donde hay botones, y nada más. No "todo vacío": un
  // rango vacío significa histórico completo, y ese default ya se descartó una
  // vez por lento. Limpiar = volver al estado
  // que el usuario reconoce como "recién abierto".
  const limpiarTodo = () => {
    setDirection('inbound');
    setDateFrom(currentWeekStart());
    setDateTo(localDate(new Date()));
    setAgent([]); setAgentExcluido(excluidosInicial); setQueue([]); setHotel([]); setCrece([]); setTariff([]);
    setSentiment([]); setCategory([]);
    setConclusion(conclusionInicial); setPreseleccion(preseleccionInicial);
    setSpam(''); setSpamSubtype([]); setComplaints(''); setComplaintTarget([]);
    setAprovechable(''); setTypification(''); setReplica('');
    setConductaSi([]); setConductaNo([]);
    setCallId('');
  };

  // Lo elegido SIEMPRE se ve, aunque el rango nuevo ya no lo ofrezca.
  //
  // Al mover el rango, las opciones de conclusión cambian (los catálogos tienen
  // una frontera de fecha). Una conclusión que quedó seleccionada y desaparece
  // de la lista sigue filtrando —viaja en los params— pero deja de verse: el
  // tablero muestra cero resultados y la causa no está en pantalla. Se agrega
  // al final para que se pueda ver y quitar; no se borra sola, que sería
  // cambiarle el filtro a alguien sin que lo pida.
  const conclusionesVisibles = [
    ...(opts.conclusions || []),
    ...conclusion.filter((c) => !(opts.conclusions || []).includes(c)),
  ];

  const presets = opts.conclusion_presets || [];
  const preseleccionInicial = preseleccionTipificacion && presets.length
    ? PRESELECCION_INICIAL : PRESELECCION_VACIA;
  const conclusionInicial = etiquetasDePreseleccion(presets, preseleccionInicial);
  const etiquetasConBoton = presets.map((p) => p.label);
  // Un rango enteramente anterior al corte no ofrece etiquetas v2: los botones
  // se ven y se explican en vez de desaparecer, igual que el resto de la barra.
  const hayV2EnElRango = presets.some((p) => (opts.conclusions || []).includes(p.label));

  // Los botones cuentan como presionados sólo mientras el dropdown diga lo
  // mismo que ellos. Si alguien quita o agrega a mano una etiqueta de las que
  // cubren, los botones se apagan: encendidos, afirmarían un filtro que ya no
  // es el que está puesto. Lo elegido a mano se queda como está.
  const conBotonSeleccionadas = conclusion.filter((c) => etiquetasConBoton.includes(c));
  const preseleccionVigente = presets.length
    && mismoConjunto(conBotonSeleccionadas, etiquetasDePreseleccion(presets, preseleccion))
    ? preseleccion : PRESELECCION_VACIA;

  // Sólo se tocan las etiquetas que tienen botón. Lo demás del dropdown —CR-*,
  // Gru, X, Seguimiento, «Llamada No Aprovechable»— se conserva.
  const alternarPreseleccion = (eje, id) => {
    const actual = preseleccionVigente[eje];
    const siguiente = {
      ...preseleccionVigente,
      [eje]: actual.includes(id) ? actual.filter((x) => x !== id) : [...actual, id],
    };
    setConclusion([
      ...conclusion.filter((c) => !etiquetasConBoton.includes(c)),
      ...etiquetasDePreseleccion(presets, siguiente),
    ]);
    setPreseleccion(siguiente);
  };

  // Qué marcaría ese botón combinado con lo que ya está presionado en el otro
  // eje. Es lo que se ve al pasar el mouse.
  const tituloDeBoton = (eje, id) => {
    if (!hayV2EnElRango) {
      return 'El rango elegido no incluye llamadas con tipificación v2 (desde el 19-ago-2026)';
    }
    const otroEje = eje === 'rubros' ? 'desenlaces' : 'rubros';
    const etiquetas = etiquetasDePreseleccion(presets, {
      [eje]: [id], [otroEje]: preseleccionVigente[otroEje],
    });
    return `Marca en «Toda conclusion»: ${etiquetas.join(', ')}`;
  };

  const hayAlgoQueLimpiar = Boolean(
    callId || (!vistaDelAgente && (agent.length || !mismoConjunto(agentExcluido, excluidosInicial))) || queue.length || hotel.length || crece.length
    || tariff.length || sentiment.length || category.length
    || !mismoConjunto(conclusion, conclusionInicial)
    || spam || spamSubtype.length || complaints || complaintTarget.length
    || aprovechable || typification || replica || conductaSi.length || conductaNo.length
    || direction !== 'inbound'
    || dateFrom !== currentWeekStart() || dateTo !== localDate(new Date()),
  );

  return (
    <div
      className="filters-bar"
      style={{
        // `column`: ahora la barra son grupos apilados, no una sola fila. El
        // `flex-wrap` de la clase servía para partir 18 controles donde
        // cupieran, que es justo lo que rompía los pares (Quejas quedaba lejos
        // de Responsable de queja).
        flexDirection: 'column', alignItems: 'stretch',
        gap: 10, marginBottom: 16,
      }}
    >
      {preseleccionTipificacion && (
        <GrupoDeFiltros titulo="Filtros pre-seleccionados">
          {RUBROS_PRESELECCION.map((b) => (
            <BotonDePreseleccion
              key={b.id}
              texto={b.texto}
              activo={preseleccionVigente.rubros.includes(b.id)}
              disabled={!hayV2EnElRango}
              title={tituloDeBoton('rubros', b.id)}
              onClick={() => alternarPreseleccion('rubros', b.id)}
            />
          ))}
          <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', background: '#2a2a38', margin: '0 4px' }} />
          {DESENLACES_PRESELECCION.map((b) => (
            <BotonDePreseleccion
              key={b.id}
              texto={b.texto}
              activo={preseleccionVigente.desenlaces.includes(b.id)}
              disabled={!hayV2EnElRango}
              title={tituloDeBoton('desenlaces', b.id)}
              onClick={() => alternarPreseleccion('desenlaces', b.id)}
            />
          ))}
        </GrupoDeFiltros>
      )}

      <GrupoDeFiltros titulo="Filtros de Llamada">
        <input
          className="filter-input"
          placeholder="ID de llamada..."
          value={callId}
          onChange={e => setCallId(e.target.value)}
          title="Buscar por ID: conversation_id de Genesys (parcial) o el # interno"
          style={{ width: 180 }}
        />
        <button
          onClick={limpiarTodo}
          disabled={!hayAlgoQueLimpiar}
          title={preseleccionInicial === PRESELECCION_INICIAL
            ? 'Quita todos los filtros y vuelve al estado inicial: semana en curso, solo entrantes, Públicas + Venta + No venta'
            : 'Quita todos los filtros y vuelve al estado inicial: semana en curso, solo entrantes'}
          style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
            cursor: hayAlgoQueLimpiar ? 'pointer' : 'default',
            border: '1px solid #2a2a38',
            background: 'transparent',
            color: hayAlgoQueLimpiar ? '#a5b4fc' : '#4b5563',
          }}
        >
          ✕ Limpiar filtros
        </button>
      </GrupoDeFiltros>

      <GrupoDeFiltros titulo="Filtros de Temporalidad y Dirección">
        <select className="filter-select" value={direction} onChange={e => setDirection(e.target.value)}>
          <option value="inbound">Entrantes</option>
          <option value="outbound">Salientes</option>
          <option value="both">Ambas</option>
        </select>
        <input className="filter-input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Desde" />
        <input className="filter-input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Hasta" />
      </GrupoDeFiltros>

      <GrupoDeFiltros titulo="Filtros de Atención">
        {!vistaDelAgente && (
          <FiltroDeAgentes
            opciones={opts.agents || []}
            elegidos={agent} setElegidos={setAgent}
            excluidos={agentExcluido} setExcluidos={setAgentExcluido}
          />
        )}
        <MultiSelectFilter label="Todas las colas" options={opts.queues || []} value={queue} onChange={setQueue} />
        <MultiSelectFilter label="Todos los hoteles" options={opts.hotels || []} value={hotel} onChange={setHotel} />
        <MultiSelectFilter label="Tipo de atención" options={opts.crece_classifications || []} value={crece} onChange={setCrece} />
      </GrupoDeFiltros>

      <GrupoDeFiltros titulo="Filtros de Categoría de llamada">
        <MultiSelectFilter label="Toda categoria" options={opts.categories || []} value={category} onChange={setCategory} />
        <MultiSelectFilter label="Toda conclusion" options={conclusionesVisibles} value={conclusion} onChange={setConclusion} />
        <MultiSelectFilter label="Tipo de tarifa" options={opts.tariff_types || []} value={tariff} onChange={setTariff} />
        {/* Spam y su subtipo, juntos: el subtipo no significa nada sin el de
            arriba, y estaban separados por dos controles ajenos. */}
        <select className="filter-select" value={spam} onChange={e => setSpam(e.target.value)} title="Spam">
          <option value="">Todas (spam)</option>
          <option value="true">Solo spam</option>
          <option value="false">Sin spam</option>
        </select>
        <MultiSelectFilter label="Subtipo spam" options={opts.spam_subtypes || []} value={spamSubtype} onChange={setSpamSubtype} />
      </GrupoDeFiltros>

      <GrupoDeFiltros titulo="Filtros de Calidad de atención">
        <select
          className="filter-select"
          value={aprovechable}
          onChange={e => setAprovechable(e.target.value)}
          title="Aprovechamiento: sirve para MIRAR las no aprovechables, no para decidir si cuentan"
        >
          <option value="">Todas (aprovechamiento)</option>
          <option value="true">Solo aprovechables</option>
          <option value="false">Solo NO aprovechables</option>
        </select>
        <MultiSelectFilter label="Todo sentimiento" options={opts.sentiments || []} value={sentiment} onChange={setSentiment} />
        {/* Quejas y su responsable, juntos por la misma razón que spam. */}
        <select className="filter-select" value={complaints} onChange={e => setComplaints(e.target.value)} title="Quejas">
          <option value="">Todas (quejas)</option>
          <option value="true">Con quejas</option>
        </select>
        <MultiSelectFilter label="Responsable queja" options={opts.complaint_targets || []} value={complaintTarget} onChange={setComplaintTarget} />
        <select
          className="filter-select"
          value={typification}
          onChange={e => setTypification(e.target.value)}
          title="Concordancia entre la tipificación del agente y la del análisis automático"
        >
          <option value="">Toda tipificación</option>
          <option value="no_tipifica">No tipificó</option>
          <option value="coincide">Tipificó · coincide con IA</option>
          <option value="difiere">Tipificó · difiere de IA</option>
          <option value="sin_comparacion">Tipificó · sin comparación</option>
        </select>
        {/* La cola de trabajo de la sesión de revisión. Sin esto la réplica
            existe y es invisible: el supervisor tendría que abrir llamadas al
            azar hasta encontrar una. */}
        <select
          className="filter-select"
          value={replica}
          onChange={e => setReplica(e.target.value)}
          title="Réplica del agente a su calificación. 'En desacuerdo' es lo que hay que revisar con él."
        >
          <option value="">Toda réplica</option>
          {/* `pendiente` y no `desacuerdo`: el desacuerdo es cierto para
              siempre, así que esta opción decía "por revisar" sobre réplicas
              que el supervisor ya había atendido — la cola no se vaciaba
              nunca. Ahora pregunta por las que no tienen resolución. */}
          <option value="pendiente">En desacuerdo — por revisar</option>
          <option value="resuelta">Ya resueltas</option>
          <option value="desacuerdo">En desacuerdo (todas)</option>
          <option value="con">Con réplica</option>
          <option value="sin">Sin réplica</option>
        </select>
      </GrupoDeFiltros>

      {/* Lo que el agente hizo o no hizo según la boleta V2 (12-oct-2026).
          Grupo propio a pedido de Armando (12-oct-2026), y SÓLO donde la
          pantalla pasa `showConducta` (Llamadas): los cinco grupos de siempre
          no cambian. Ver test_filtro_replica_y_grupos.
          Con varias elegidas se exigen TODAS: «no presente: Sondear + Perfilar»
          son las llamadas donde no hubo ninguna de las dos. Sólo entran
          llamadas con boleta V2 visible, y un criterio que no aplicó (N/A) no
          cuenta como presente ni como ausente. */}
      {showConducta && (
        <GrupoDeFiltros titulo="Filtros de Conducta">
          {/* Los dos selectores viven en `FiltrosDeConducta`. En Mis llamadas
              del Agente (`vistaDelAgente`) hablan en primera persona. */}
          <FiltrosDeConducta
            catalogo={opts.conductas_v2 || []}
            presentes={conductaSi}
            ausentes={conductaNo}
            onPresentes={setConductaSi}
            onAusentes={setConductaNo}
            primeraPersona={vistaDelAgente}
          />
        </GrupoDeFiltros>
      )}
    </div>
  );
}
